import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardMedia,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Alert,
  Button,
  TextField
} from '@mui/material';
import { useAuth } from '../components/AuthProvider';
import { useCart } from '../components/CartProvider';

const API_BASE = process.env.REACT_APP_API_URL;

interface Product {
  id: number;
  productName: string;
  details: string;
  description: string;
  variationMap: {
    [key: string]: {
      variationName: string;
      variationDescription: string;
      primaryImage: string;
      priceUSD: number;
      images: string[];
      customizable: boolean;
    };
  };
}

const ProductDetail: React.FC<{ openCartDrawer?: () => void }> = ({ openCartDrawer }) => {
  const { isAdmin } = useAuth();
  const { addToCart } = useCart();
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedVariation, setSelectedVariation] = useState<string>('');
  const [searchParams] = useSearchParams();
  const [showBanner, setShowBanner] = useState(false);
  const [customRequest, setCustomRequest] = useState('');
  const navigate = useNavigate();

  const getDefaultVariation = (p: any, vid: any) => vid || Object.keys(p.variationMap)[0];
  const getDefaultImage = (p: any, vid: any) =>
    p.variationMap[getDefaultVariation(p, vid)].primaryImage;

  const handleVariationChange = (event: SelectChangeEvent<string>) => {
    setSelectedVariation(event.target.value);
    if (product) {
      setSelectedImage(product.variationMap[event.target.value].primaryImage);
    }
    setCustomRequest('');
  };

  const handleEditClick = () => {
    navigate(`/admin/product/${id}`);
  };

  const handleAddToCart = () => {
    const varData = product!.variationMap[selectedVariation];
    addToCart({
      productId: product!.id,
      variationId: parseInt(selectedVariation, 10),
      quantity: 1,
      name: `${product!.productName} - ${varData.variationName}`,
      price: varData.priceUSD,
      primaryImage: varData.primaryImage,
      customDetails: varData.customizable ? customRequest : undefined
    });
    openCartDrawer?.();
  };

  useEffect(() => {
    const fetchProduct = async () => {
      const response = await axios.get(`${API_BASE}/api/products/${id}`);
      setProduct(response.data as Product);
      const defaultVar = getDefaultVariation(response.data, searchParams.get('variation'));
      setSelectedVariation(defaultVar);
      setSelectedImage(getDefaultImage(response.data, searchParams.get('variation')));
    };
    fetchProduct();

    if (searchParams.get('success') === 'true') {
      setShowBanner(true);
    }
  }, [id, searchParams]);

  if (!product || !selectedVariation) {
    return <Typography variant="h6">Loading...</Typography>;
  }

  const variation = product.variationMap[selectedVariation];

  return (
    <div>
      {showBanner && isAdmin && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Product successfully added!
        </Alert>
      )}
      <Container sx={{ marginTop: 4, marginBottom: 4}}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardMedia component="img" height="500" image={selectedImage} alt={product.productName} />
            </Card>
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              {variation.images.map((image, index) => (
                <Grid item xs={3} key={index}>
                  <Card onClick={() => setSelectedImage(image)} style={{ cursor: 'pointer' }}>
                    <CardMedia component="img" height="100" image={image} alt={`Product Image ${index + 1}`} />
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} sx={{mb: 4}}>
            <h2 style={{ display: 'inline-block', marginRight: '8px' }}>
              {isAdmin ? (
                <span
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={handleEditClick}
                  title="Edit"
                >
                  {product.productName}
                </span>
              ) : (
                product.productName
              )}
            </h2>
            <Typography variant="h6" color="text.secondary" sx={{ marginTop: 2 }}>
              ${variation.priceUSD}
            </Typography>
            <Typography variant="body1" sx={{ marginTop: 2 }}>
              {product.description}
            </Typography>
            <Typography variant="body1" sx={{ marginTop: 2 }}>
              {product.details}
            </Typography>
            <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
              <Select value={selectedVariation} onChange={handleVariationChange}>
                {Object.entries(product.variationMap).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value.variationName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {variation.customizable && (
              <TextField
                label="Custom Request"
                value={customRequest}
                onChange={(e) => setCustomRequest(e.target.value)}
                fullWidth
                multiline
                rows={2}
                sx={{ m: 1 }}
              />
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleAddToCart}
              sx={{ m: 1 }}
            >
              Add to Cart
            </Button>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default ProductDetail;
